import React, { useState, useCallback, useEffect } from "react";
import {
    Box, Typography, TextField, IconButton, Avatar, Select, MenuItem,
    Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button,
    Card, Skeleton, Tooltip, InputAdornment, Chip,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import SearchIcon           from "@mui/icons-material/Search";
import KeyboardArrowLeft    from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight   from "@mui/icons-material/KeyboardArrowRight";
import DeleteIcon           from "@mui/icons-material/Delete";
import ClearIcon            from "@mui/icons-material/Clear";
import OpenInNewIcon        from "@mui/icons-material/OpenInNew";
import PersonRoundedIcon    from "@mui/icons-material/PersonRounded";
import PhoneRoundedIcon     from "@mui/icons-material/PhoneRounded";
import EmailRoundedIcon     from "@mui/icons-material/EmailOutlined";
import LocationOnRoundedIcon from "@mui/icons-material/LocationOnRounded";
import { API_ENDPOINTS, getData, postData } from "../../services/apiService.js";
import { encryptId } from "../helper/idCodec.js";
import { useNavigate } from "react-router-dom";

// ── Brand palette ─────────────────────────────────────────────────────────
const T = {
    teal:    "#007C84", tealDk: "#016268", tealLt: "#E0F4F5",
    orange:  "#FF7900", orangeLt: "#FFF0E0",
    emerald: "#059669", emeraldLt: "#D1FAE5",
    rose:    "#E11D48", roseLt:   "#FFE4E6",
    indigo:  "#4F46E5", indigoLt: "#EEF2FF",
    amber:   "#D97706", amberLt:  "#FEF3C7",
    bg:      "#F7FAFC", border: "#E2E8F0",
    text:    "#0F172A", muted:  "#64748B", surface: "#FFFFFF",
};

const AVATAR_PALETTE = [
    [T.tealLt, T.teal], [T.orangeLt, T.orange], [T.indigoLt, T.indigo],
    [T.emeraldLt, T.emerald], [T.amberLt, T.amber], [T.roseLt, T.rose],
];
const avatarBg = (name = "") => AVATAR_PALETTE[name.charCodeAt(0) % AVATAR_PALETTE.length];
const initials = (name = "") => { const p = name.trim().split(" "); return p.length >= 2 ? (p[0][0]+p[1][0]).toUpperCase() : (p[0]?.[0] ?? "?").toUpperCase(); };

const SkelRow = () => (
    <TableRow>
        {[200, 160, 120, 140, 40].map((w, i) => <TableCell key={i}><Skeleton width={w} height={18} /></TableCell>)}
    </TableRow>
);

export default function ManageUsers() {
    const [users,          setUsers]          = useState([]);
    const [loading,        setLoading]        = useState(true);
    const [error,          setError]          = useState(null);

    // Two separate search fields
    const [nameSearch,     setNameSearch]     = useState("");
    const [mobileSearch,   setMobileSearch]   = useState("");
    const [appliedSearch,  setAppliedSearch]  = useState("");  // sent to API

    const [page,           setPage]           = useState(0);
    const [perPage,        setPerPage]        = useState(15);
    const [selectedIds,    setSelectedIds]    = useState([]);
    const [deleteOpen,     setDeleteOpen]     = useState(false);

    const navigate = useNavigate();

    const fetchUsers = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const params = {};
            if (appliedSearch) params.search = appliedSearch;
            const data = await getData(API_ENDPOINTS.getUsersList, params);
            if (data.status === "SUCCESS" && data.result) {
                setUsers(data.result.map(c => ({
                    id:     c.customer_id,
                    name:   `${c.first_name || ""} ${c.last_name || ""}`.trim(),
                    email:  c.email || "",
                    avatar: c.pic || undefined,
                    mobile: c.mobile_num,
                    city:   c.city || "",
                    state:  c.state || "",
                    createdAt: c.createdAt,
                })));
            } else {
                setError(data.message || "Failed to fetch customers.");
            }
        } catch (_) { setError("An error occurred while fetching customers."); }
        finally     { setLoading(false); }
    }, [appliedSearch]);

    useEffect(() => { fetchUsers(); }, [fetchUsers]);

    const applyNameSearch  = () => { if (nameSearch.trim()) { setMobileSearch(""); setAppliedSearch(nameSearch.trim()); setPage(0); } };
    const applyMobileSearch= () => { if (mobileSearch.trim()) { setNameSearch(""); setAppliedSearch(mobileSearch.trim()); setPage(0); } };
    const clearAll         = () => { setNameSearch(""); setMobileSearch(""); setAppliedSearch(""); setPage(0); };

    const totalPages = Math.ceil(users.length / perPage);
    const paginated  = users.slice(page * perPage, (page + 1) * perPage);

    const handleDelete = useCallback(async () => {
        setDeleteOpen(false);
        if (!selectedIds.length) return;
        try {
            const res = await postData(API_ENDPOINTS.deleteMultipleUsers, { customer_ids: selectedIds });
            if (res.status === "SUCCESS") { setUsers(prev => prev.filter(u => !selectedIds.includes(u.id))); setSelectedIds([]); }
            else setError(res.message || "Failed to delete.");
        } catch (_) { setError("Error deleting customers."); }
    }, [selectedIds]);

    return (
        <Box sx={{ bgcolor: T.bg, minHeight: "100vh", pb: 4 }}>

            {/* ── Hero header ── */}
            <Box sx={{
                background: `linear-gradient(135deg, ${T.teal} 0%, ${T.tealDk} 100%)`,
                borderRadius: "20px", p: 3, mb: 3,
                display: "flex", justifyContent: "space-between", alignItems: "center",
                boxShadow: "0 4px 20px rgba(0,124,132,0.2)",
            }}>
                <Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#fff", fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                        Customers
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "rgba(255,255,255,0.75)", mt: 0.5 }}>
                        All registered customers on the platform
                    </Typography>
                </Box>
                {!loading && (
                    <Box sx={{ display: "flex", gap: 1.5 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, bgcolor: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: "10px", px: 1.5, py: 0.7 }}>
                            <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#fff", lineHeight: 1, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{users.length}</Typography>
                            <Typography sx={{ fontSize: 11, fontWeight: 600, color: "rgba(255,255,255,0.85)" }}>Total</Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* ── Search toolbar ── */}
            <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 2.5 }}>
                {/* Name search */}
                <TextField
                    placeholder="Search by name…"
                    size="small" value={nameSearch}
                    onChange={e => setNameSearch(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") applyNameSearch(); }}
                    sx={{ flex: "1 1 200px", maxWidth: 300 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><PersonRoundedIcon sx={{ fontSize: 16, color: T.muted }} /></InputAdornment>,
                        endAdornment: nameSearch ? <InputAdornment position="end"><IconButton size="small" onClick={() => { setNameSearch(""); if (appliedSearch === nameSearch) clearAll(); }}><ClearIcon sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                        sx: { borderRadius: "12px", bgcolor: T.surface, fontSize: 13 },
                    }}
                />

                {/* Mobile search */}
                <TextField
                    placeholder="Search by mobile…"
                    size="small" value={mobileSearch}
                    onChange={e => setMobileSearch(e.target.value.replace(/\D/g, ""))}
                    onKeyDown={e => { if (e.key === "Enter") applyMobileSearch(); }}
                    sx={{ flex: "1 1 180px", maxWidth: 260 }}
                    InputProps={{
                        startAdornment: <InputAdornment position="start"><PhoneRoundedIcon sx={{ fontSize: 16, color: T.muted }} /></InputAdornment>,
                        endAdornment: mobileSearch ? <InputAdornment position="end"><IconButton size="small" onClick={() => { setMobileSearch(""); if (appliedSearch === mobileSearch) clearAll(); }}><ClearIcon sx={{ fontSize: 14 }} /></IconButton></InputAdornment> : null,
                        sx: { borderRadius: "12px", bgcolor: T.surface, fontSize: 13 },
                    }}
                />

                <Button variant="contained" disableElevation size="small"
                    onClick={() => { if (nameSearch.trim()) applyNameSearch(); else if (mobileSearch.trim()) applyMobileSearch(); }}
                    startIcon={<SearchIcon />}
                    sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, bgcolor: T.teal, "&:hover": { bgcolor: T.tealDk }, height: 40 }}>
                    Search
                </Button>

                {appliedSearch && (
                    <Chip label={`"${appliedSearch}"`} onDelete={clearAll} size="small"
                        sx={{ bgcolor: T.tealLt, color: T.teal, fontWeight: 600, borderRadius: "8px", border: `1px solid ${T.teal}22` }} />
                )}

                {selectedIds.length > 0 && (
                    <Button variant="contained" color="error" size="small" disableElevation
                        startIcon={<DeleteIcon />} onClick={() => setDeleteOpen(true)}
                        sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, ml: "auto" }}>
                        Delete ({selectedIds.length})
                    </Button>
                )}

                {!loading && (
                    <Typography sx={{ fontSize: 13, color: T.muted, ml: selectedIds.length > 0 ? 0 : "auto" }}>
                        {users.length} customer{users.length !== 1 ? "s" : ""}
                    </Typography>
                )}
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2, borderRadius: "12px" }}>{error}</Alert>}

            {/* ── Table ── */}
            <Card sx={{ borderRadius: "16px", border: `1px solid ${T.border}`, boxShadow: "0 1px 4px rgba(15,23,42,0.06)", overflow: "hidden" }}>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: T.bg }}>
                                {["Customer", "Email", "Mobile", "Location", ""].map(h => (
                                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: 11, color: T.muted, textTransform: "uppercase", letterSpacing: "0.4px", py: 1.5, borderBottom: `2px solid ${T.border}` }}>
                                        {h}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading
                                ? Array.from({ length: 10 }).map((_, i) => <SkelRow key={i} />)
                                : paginated.length === 0
                                ? (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                                            <PersonRoundedIcon sx={{ fontSize: 40, color: T.border, mb: 1, display: "block", mx: "auto" }} />
                                            <Typography fontSize={13} color="text.secondary">
                                                {appliedSearch ? `No customers matching "${appliedSearch}".` : "No customers found."}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )
                                : paginated.map(user => {
                                    const [bg, fg] = avatarBg(user.name);
                                    const location = [user.city, user.state].filter(Boolean).join(", ") || "—";
                                    return (
                                        <TableRow key={user.id} hover
                                            sx={{ cursor: "pointer", "&:last-child td": { border: 0 }, "&:hover": { bgcolor: T.tealLt + "44" } }}
                                            onClick={() => navigate(`/app/user-details/${encryptId(user.id)}`)}>

                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                    <Avatar src={user.avatar} sx={{ width: 36, height: 36, bgcolor: bg, color: fg, fontSize: 13, fontWeight: 800 }}>
                                                        {!user.avatar && initials(user.name)}
                                                    </Avatar>
                                                    <Box>
                                                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: T.text }}>{user.name}</Typography>
                                                        <Typography sx={{ fontSize: 11, color: T.muted }}>ID #{user.id}</Typography>
                                                    </Box>
                                                </Box>
                                            </TableCell>

                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                                                    <EmailRoundedIcon sx={{ fontSize: 13, color: T.muted }} />
                                                    <Typography sx={{ fontSize: 12, color: user.email ? T.text : T.muted }}>{user.email || "Not provided"}</Typography>
                                                </Box>
                                            </TableCell>

                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                                                    <PhoneRoundedIcon sx={{ fontSize: 13, color: T.muted }} />
                                                    <Typography sx={{ fontSize: 12, color: T.text, fontWeight: 500 }}>{user.mobile}</Typography>
                                                </Box>
                                            </TableCell>

                                            <TableCell>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.7 }}>
                                                    <LocationOnRoundedIcon sx={{ fontSize: 13, color: T.muted }} />
                                                    <Typography sx={{ fontSize: 12, color: T.muted }}>{location}</Typography>
                                                </Box>
                                            </TableCell>

                                            <TableCell padding="none">
                                                <Tooltip title="View profile">
                                                    <IconButton size="small"
                                                        onClick={e => { e.stopPropagation(); navigate(`/app/user-details/${encryptId(user.id)}`); }}
                                                        sx={{ color: T.muted }}>
                                                        <OpenInNewIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            }
                        </TableBody>
                    </Table>
                </TableContainer>

                {/* Pagination */}
                {!loading && users.length > 0 && (
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 2.5, py: 1.5, borderTop: `1px solid ${T.border}` }}>
                        <Typography sx={{ fontSize: 13, color: T.muted }}>
                            Showing {Math.min(page * perPage + 1, users.length)}–{Math.min((page + 1) * perPage, users.length)} of {users.length}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Select value={perPage} onChange={e => { setPerPage(Number(e.target.value)); setPage(0); }} size="small" sx={{ borderRadius: "10px", fontSize: 13 }}>
                                {[15, 25, 50].map(n => <MenuItem key={n} value={n}>{n} per page</MenuItem>)}
                            </Select>
                            <IconButton size="small" onClick={() => setPage(p => Math.max(p-1, 0))} disabled={page === 0}
                                sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                                <KeyboardArrowLeft sx={{ fontSize: 18 }} />
                            </IconButton>
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: T.text, minWidth: 56, textAlign: "center" }}>
                                {page + 1} / {totalPages}
                            </Typography>
                            <IconButton size="small" onClick={() => setPage(p => Math.min(p+1, totalPages-1))} disabled={page >= totalPages-1}
                                sx={{ bgcolor: T.bg, border: `1px solid ${T.border}`, borderRadius: "8px" }}>
                                <KeyboardArrowRight sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Box>
                    </Box>
                )}
            </Card>

            {/* Delete dialog */}
            <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} PaperProps={{ sx: { borderRadius: "16px" } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <Typography fontSize={14}>Delete {selectedIds.length} selected customer{selectedIds.length > 1 ? "s" : ""}? This cannot be undone.</Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button onClick={() => setDeleteOpen(false)} sx={{ borderRadius: "10px", textTransform: "none" }}>Cancel</Button>
                    <Button onClick={handleDelete} color="error" variant="contained" disableElevation sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>Delete</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
